import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTrialInfo } from "@/hooks/useTrialInfo";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import SharedFooter from "@/components/SharedFooter";
import SEO, { organizationSchema, educationalOrganizationSchema } from "@/components/SEO";
import { API } from "@/App";
import {
  Star,
  ArrowRight,
  Play,
  MessageCircle,
  GraduationCap,
  Users,
  Shield,
  Sparkles,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  LogIn,
  CreditCard,
  Monitor,
  Bell,
  Clock,
  Smartphone,
  School,
  ShoppingCart,
  Lightbulb,
  Menu,
  X,
  BookOpen,
  Gift,
  Zap,
  Brain,
  FileQuestion,
  Layers,
  Globe,
  Target,
  Award,
  Percent,
} from "lucide-react";
import { FloatingElements } from "@/components/ui/animations";

export default function LandingPage() {
  const navigate = useNavigate();
  const { trialDays } = useTrialInfo();
  const { getCartCount, setIsCartOpen } = useCart();
  const cartCount = getCartCount();
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [eduOneOpen, setEduOneOpen] = useState(false);
  const aiCentralRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (aiCentralRef.current && !aiCentralRef.current.contains(event.target)) {
        setEduOneOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWhatsAppWaitlist = async (e) => {
    e.preventDefault();
    if (!whatsappPhone || whatsappPhone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/waitlist/whatsapp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: whatsappPhone })
      });
      
      if (response.ok) {
        setWaitlistJoined(true);
        toast.success("You're on the WhatsApp waitlist! We'll notify you when it launches.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      // Even if API fails, show success for now (will implement backend)
      setWaitlistJoined(true);
      toast.success("You're on the WhatsApp waitlist!");
    }
    setIsSubmitting(false);
  };

  // Combined structured data for homepage
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      educationalOrganizationSchema,
      {
        "@type": "WebSite",
        "name": "Eduzy",
        "url": "https://eduzy.co",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://eduzy.co/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Eduzy - AI-Powered Learning Platform for Grades 5-8 (CBSE & IB) | Personalized Tutoring"
        description="Transform your child's learning with Eduzy's AI-powered tutoring platform. Personalized lessons, homework help, and practice tests for grades 5-8 across CBSE and IB curricula in 11 Indian languages. Start your free trial today!"
        keywords="AI tutor India, online learning grades 5-8, CBSE homework help, IB curriculum tutoring, IB MYP PYP, personalized education, AI tutoring platform, EduWise, EduAssist, question bank, adaptive learning"
        canonicalUrl="/"
        structuredData={homepageStructuredData}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 overflow-hidden">
        {/* Background decorative elements - enhanced with color variety */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-yellow-300/30 rounded-full blur-3xl animate-hero-float-1"></div>
          <div className="absolute top-1/3 -left-20 w-72 h-72 bg-pink-400/25 rounded-full blur-3xl animate-hero-float-2"></div>
          <div className="absolute bottom-20 right-1/4 w-56 h-56 bg-purple-400/25 rounded-full blur-2xl animate-hero-float-3"></div>
          <div className="absolute top-1/4 right-1/3 w-40 h-40 bg-orange-300/20 rounded-full blur-2xl animate-hero-float-1"></div>
          
          {/* Animated floating education icons */}
          <FloatingElements />
        </div>

        {/* Navigation */}
        <nav className="relative z-[100] px-4 sm:px-6 lg:px-8 py-2 bg-white/95 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            {/* Logo Section */}
            <div className="flex items-center gap-1">
              <img 
                src="/eduzy-logo-transparent.png" 
                alt="Eduzy" 
                className="h-16 sm:h-20 w-auto cursor-pointer"
                data-testid="header-logo"
                onClick={() => navigate("/")}
              />
              <a 
                href="https://contentiumtech.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-100 opacity-80 transition-opacity hidden sm:block"
              >
                <img 
                  src="/contentium-tech-logo.svg" 
                  alt="Contentium Tech" 
                  className="h-auto"
                  style={{ height: '3.5rem' }}
                  data-testid="header-contentium-logo"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              {/* EduOne AI Dropdown */}
              <div className="relative" ref={aiCentralRef}>
                <Button
                  onMouseEnter={() => setEduOneOpen(true)}
                  onClick={() => setEduOneOpen(!eduOneOpen)}
                  variant="ghost"
                  className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
                  data-testid="nav-eduone-btn"
                  title="For Individual Students & Parents"
                >
                  <Sparkles className="w-4 h-4" />
                  EduOne AI
                  <ChevronDown className={`w-4 h-4 transition-transform ${eduOneOpen ? 'rotate-180' : ''}`} />
                </Button>
                
                {/* EduOne AI Dropdown Menu */}
                {eduOneOpen && (
                  <div 
                    className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9999]"
                    onMouseLeave={() => setEduOneOpen(false)}
                  >
                    <button
                      onClick={() => { navigate("/eduwise"); setEduOneOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                      data-testid="dropdown-eduwise"
                    >
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">EduWise</div>
                        <div className="text-xs text-gray-500">Personalized AI Tutor</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { navigate("/eduassist"); setEduOneOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50 transition-colors"
                      data-testid="dropdown-eduassist"
                    >
                      <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Lightbulb className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">EduAssist</div>
                        <div className="text-xs text-gray-500">Personal AI Homework Assistant</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { navigate("/eduquest"); setEduOneOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors"
                      data-testid="dropdown-eduquest"
                    >
                      <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FileQuestion className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">EduQuest</div>
                        <div className="text-xs text-gray-500">Book-Grounded Question Banks</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* EduSphere AI Button with School Sphere Icon */}
              <Button
                onClick={() => navigate("/edusphere")}
                variant="ghost"
                className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
                data-testid="nav-edusphere-btn"
                title="For Schools & Institutions"
              >
                {/* Custom School Sphere Icon */}
                <div className="relative w-4 h-4">
                  <div className="absolute inset-0 border-2 border-indigo-400 rounded-full"></div>
                  <School className="w-2.5 h-2.5 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                EduSphere AI
              </Button>

              {/* Blog Button */}
              <Button
                onClick={() => navigate("/blog")}
                variant="ghost"
                className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10"
                data-testid="nav-blog-btn"
              >
                <BookOpen className="w-4 h-4" />
                Blog
              </Button>

              {/* Cart Button */}
              <Button
                onClick={() => setIsCartOpen(true)}
                variant="ghost"
                className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10 relative"
                data-testid="nav-cart-btn"
              >
                <ShoppingCart className="w-4 h-4" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Button>

              <div className="w-px h-6 bg-gray-200 mx-1"></div>
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-4 py-2 h-10"
                data-testid="nav-login-btn"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Login
              </Button>
            </div>

            {/* Mobile: Cart + Login + Hamburger */}
            <div className="flex lg:hidden items-center gap-2">
              <Button
                onClick={() => setIsCartOpen(true)}
                size="sm"
                variant="ghost"
                className="text-gray-600 hover:bg-gray-100 px-2 relative"
                data-testid="nav-cart-btn-mobile"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Button>
              <Button
                onClick={() => navigate("/login")}
                size="sm"
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-3 py-2 h-auto"
                data-testid="nav-login-btn-mobile"
              >
                <LogIn className="w-4 h-4 mr-1" />
                Login
              </Button>
              <Button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                size="sm"
                variant="ghost"
                className="text-gray-700 px-2"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden absolute left-0 right-0 top-full bg-white border-b border-gray-200 shadow-lg animate-in slide-in-from-top-2 duration-200 max-h-[70vh] overflow-y-auto z-[9999]">
              <div className="px-4 py-3 space-y-1">
                {/* EduOne AI Section */}
                <div className="bg-emerald-50 rounded-lg p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    <span className="font-bold text-emerald-800">EduOne AI</span>
                  </div>
                  <div className="grid grid-cols-1 gap-1 ml-2">
                    <button
                      onClick={() => { navigate("/eduwise"); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                      data-testid="mobile-nav-eduwise"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">EduWise</div>
                        <div className="text-xs text-gray-500">Personalized AI Tutor</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { navigate("/eduassist"); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                      data-testid="mobile-nav-eduassist"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">EduAssist</div>
                        <div className="text-xs text-gray-500">Personal AI Homework Assistant</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { navigate("/eduquest"); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                      data-testid="mobile-nav-eduquest"
                    >
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FileQuestion className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">EduQuest</div>
                        <div className="text-xs text-gray-500">Book-Grounded Question Banks</div>
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* EduSphere AI */}
                <button
                  onClick={() => { navigate("/edusphere"); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  data-testid="mobile-nav-edusphere"
                >
                  <div className="relative w-5 h-5">
                    <div className="absolute inset-0 border-2 border-indigo-400 rounded-full"></div>
                    <School className="w-3 h-3 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <span className="font-semibold text-indigo-700">EduSphere AI</span>
                </button>
                
                {/* Blog */}
                <button
                  onClick={() => { navigate("/blog"); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  data-testid="mobile-nav-blog"
                >
                  <BookOpen className="w-5 h-5" />
                  <span className="font-medium">Blog</span>
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-16">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-5 animate-bounce-slow">
              <Monitor className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">AI-Powered Learning for Grades 5-8 • CBSE &amp; IB</span>
            </div>

            {/* Main Headline - with animation */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
              <span className="inline-block animate-fade-in-up" style={{ animationDelay: '0.1s' }}>Your Child's</span>
              <br />
              <span className="inline-block animate-fade-in-up" style={{ animationDelay: '0.2s' }}>Personal AI Tutor</span>
              <br />
              <span className="inline-block text-yellow-300 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>Available Now</span>
            </h1>

            {/* Subtext */}
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-4 leading-relaxed">
              Smart, conversational learning that adapts to your child - in their language, at their pace.
            </p>

            {/* Curriculum badge - CBSE & IB */}
            <div className="flex items-center justify-center mb-5" data-testid="hero-curriculum-badge">
              <div className="inline-flex items-center gap-2 bg-yellow-300/95 text-emerald-900 rounded-full px-4 py-1.5 shadow-md text-sm font-bold">
                <GraduationCap className="w-4 h-4" />
                <span>Now supporting CBSE &amp; IB curricula</span>
              </div>
            </div>

            {/* Platform Status Badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
              {/* Web Portal - Live */}
              <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-5 py-3 shadow-lg animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <Monitor className="w-5 h-5 text-emerald-600" />
                <span className="font-bold text-emerald-700">Web Portal</span>
                <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">LIVE</span>
              </div>
              
              {/* WhatsApp - Coming Soon */}
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-3 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <MessageCircle className="w-5 h-5 text-green-300" />
                <span className="font-medium text-white">WhatsApp</span>
                <span className="bg-amber-400 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  SOON
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
              <Button
                onClick={() => navigate("/login?register=true")}
                className="w-full sm:w-auto bg-white text-emerald-600 hover:bg-gray-100 font-bold text-lg px-8 py-6 rounded-full shadow-lg shadow-black/20"
                data-testid="hero-start-trial-btn"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate("/why-eduzy")}
                variant="outline"
                className="w-full sm:w-auto bg-transparent border-2 border-white/50 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-full"
                data-testid="hero-why-eduzy-btn"
              >
                <Play className="w-5 h-5 mr-2 fill-white" />
                Why Eduzy
              </Button>
            </div>

            {/* Trial info */}
            <p className="text-white/80 text-sm">
              {trialDays}-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Features Section - Why Eduzy */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              What Eduzy Offers
            </h2>
          </div>

          {/* Main Feature Cards - 3 Pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* EduWise - Personalized AI Tutor */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">EduWise</h3>
                  <p className="text-xs text-gray-500">Personalized AI Tutor</p>
                  <span className="text-xs text-blue-600 font-medium">SUBSCRIPTION</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Personalized AI tutor that adapts to your child's learning pace and style.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>Adaptive CBSE &amp; IB curriculum for each student</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>Real-time progress tracking</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>11 Indian languages supported</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-blue-200 flex items-center justify-between">
                <div>
                  <span className="text-xl font-bold text-gray-900">₹499</span>
                  <span className="text-gray-500 text-sm">/subject/month</span>
                </div>
                <Button
                  onClick={() => navigate("/eduwise")}
                  variant="ghost"
                  className="text-blue-600 hover:bg-blue-100 font-semibold text-sm px-3 py-1.5 h-auto"
                  data-testid="explore-eduwise-btn"
                >
                  Explore More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* EduAssist - Personal AI Homework Assistant */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200 hover:shadow-lg transition-shadow relative">
              <span className="absolute -top-2 right-4 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                POPULAR
              </span>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">EduAssist</h3>
                  <p className="text-xs text-gray-500">Personal AI Homework Assistant</p>
                  <span className="text-xs text-purple-600 font-medium">PAY-PER-USE</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Instant homework help with step-by-step guidance — teaches, never just answers!
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span>Photo upload with OCR recognition</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span>Socratic method — guides, not answers</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span>Unlimited follow-up questions</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-purple-200 flex items-center justify-between">
                <div>
                  <span className="text-xl font-bold text-gray-900">15 Free</span>
                  <span className="text-gray-500 text-sm"> then ₹3-4/help</span>
                </div>
                <Button
                  onClick={() => navigate("/eduassist")}
                  variant="ghost"
                  className="text-purple-600 hover:bg-purple-100 font-semibold text-sm px-3 py-1.5 h-auto"
                  data-testid="explore-eduassist-btn"
                >
                  Explore More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* EduQuest - Book-Grounded Question Banks */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <FileQuestion className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">EduQuest</h3>
                  <p className="text-xs text-gray-500">Book-Grounded Question Banks</p>
                  <span className="text-xs text-amber-600 font-medium">ONE-TIME PURCHASE</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Printable worksheets and question banks drawn from our CBSE-aligned, book-grounded bank.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                  <span>25-question worksheets (₹25)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                  <span>50-question banks (₹50)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                  <span>Instant PDF download</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-amber-200 flex items-center justify-between">
                <div>
                  <span className="text-xl font-bold text-gray-900">From ₹25</span>
                  <span className="text-gray-500 text-sm"> /worksheet</span>
                </div>
                <Button
                  onClick={() => navigate("/eduquest")}
                  variant="ghost"
                  className="text-amber-600 hover:bg-amber-100 font-semibold text-sm px-3 py-1.5 h-auto"
                  data-testid="explore-eduquest-btn"
                >
                  Explore More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>

          {/* Additional Features Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Web Portal */}
            <div className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-200 relative">
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                LIVE
              </span>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2 mt-1">
                <Monitor className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Web Portal</h3>
              <p className="text-gray-600 text-xs">Full learning on any device</p>
            </div>

            {/* Parent Dashboard */}
            <div className="text-center p-4 rounded-xl bg-indigo-50 border border-indigo-100">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Target className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">Parent Dashboard</h3>
              <p className="text-gray-600 text-xs">Track progress & insights</p>
            </div>

            {/* 11 Languages */}
            <div className="text-center p-4 rounded-xl bg-orange-50 border border-orange-100">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Layers className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">11 Languages</h3>
              <p className="text-gray-600 text-xs">Hindi, Tamil, Telugu & more</p>
            </div>

            {/* WhatsApp Coming Soon */}
            <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-200 relative opacity-80">
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5" /> SOON
              </span>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2 mt-1">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">WhatsApp</h3>
              <p className="text-gray-600 text-xs">Learning where kids are</p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section - Compact */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Learning in Your Child's Language
          </h3>
          <div className="flex flex-wrap justify-center gap-2">
            {["English", "Hindi", "Tamil", "Telugu", "Kannada", "Marathi", "Punjabi", "Bengali", "Gujarati", "Malayalam", "Odia"].map((lang) => (
              <span
                key={lang}
                className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm font-medium border border-gray-200 shadow-sm"
              >
                {lang}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview - Compact */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Simple, Transparent Pricing
          </h2>
          <p className="text-gray-600 mb-5">
            Start with a {trialDays}-day free trial. No credit card needed.
          </p>

          <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-baseline justify-center gap-2 mb-1">
              <span className="text-4xl font-bold">₹499<sup className="text-xl">*</sup></span>
              <span className="text-white/80 text-sm">/child/subject/month</span>
            </div>
            <p className="text-emerald-100 text-xs mb-1">Starting from Starter plan</p>
            <p className="text-emerald-200 text-xs mb-2">+ 18% GST</p>
            
            {/* Multi-child discount highlight */}
            <div className="bg-white/20 rounded-lg py-2 px-4 inline-block mb-4">
              <span className="text-sm font-semibold">
                <Percent className="w-4 h-4 inline mr-1" />
                20% OFF for 2nd child onwards!
              </span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mb-4 text-sm">
              {[
                "AI-powered lessons",
                "Parent dashboard",
                "Progress tracking",
                "11 languages"
              ].map((feature) => (
                <span key={feature} className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-emerald-200" />
                  {feature}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate("/login?register=true")}
                className="bg-white text-emerald-600 hover:bg-gray-100 font-bold px-6 py-2.5 rounded-full"
              >
                Start Free Trial
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                onClick={() => navigate("/eduwise")}
                variant="outline"
                className="border-white/50 text-white hover:bg-white/20 font-semibold px-5 py-2.5 rounded-full"
                data-testid="view-all-plans-btn"
              >
                View All Plans
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Compact */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-0.5">DPDP Compliant</h3>
              <p className="text-xs text-gray-600">Data protection for minors</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-0.5">Grades 5-8</h3>
              <p className="text-xs text-gray-600">CBSE &amp; IB aligned</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Monitor className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-0.5">Any Device</h3>
              <p className="text-xs text-gray-600">Works everywhere</p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-6 max-w-3xl mx-auto" data-testid="curriculum-disclaimer">
            Eduzy aligns its AI-generated content to your selected board (CBSE or IB) to the best of its ability. As content is dynamically generated, occasional variations from the official board syllabus may occur. See our <a href="/terms" className="underline hover:text-gray-600">Terms of Service</a> for details.
          </p>
        </div>
      </section>
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1.5 mb-3">
            <MessageCircle className="w-4 h-4 text-white" />
            <span className="text-white font-medium text-sm">WhatsApp Coming Soon</span>
          </div>
          
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Get Notified When WhatsApp Goes Live
          </h2>

          {!waitlistJoined ? (
            <form onSubmit={handleWhatsAppWaitlist} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="tel"
                  placeholder="Your WhatsApp number"
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                  className="pl-10 h-11 text-sm rounded-full border-0 shadow-lg"
                  data-testid="whatsapp-waitlist-input"
                />
              </div>
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="h-11 px-6 bg-white text-teal-600 hover:bg-gray-100 font-bold rounded-full shadow-lg text-sm"
                data-testid="whatsapp-waitlist-btn"
              >
                {isSubmitting ? "..." : (
                  <>
                    <Bell className="w-4 h-4 mr-1" />
                    Notify Me
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 max-w-md mx-auto">
              <CheckCircle className="w-8 h-8 text-white mx-auto mb-2" />
              <p className="font-bold text-white">You're on the list!</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
